import React, { useEffect } from 'react'

import { AppBar, Box, Toolbar } from '@mui/material'

import Button from '../Button/Button.component'
import ResponsiveDisplayWrapper from '../ResponsiveDisplayWrapper/ResponsiveDisplayWrapper.component'
import RoleSwitcher from '../RoleSwitcher/RoleSwitcher.component'
import AuthButtons from './components/AuthButtons/AuthButtons.component'
import OverFlowMenu from './components/OverFlowMenu/OverFlowMenu.component'
import ProfileMenu from './components/ProfileMenu/ProfileMenu.component'

import images from '../../../assets/images'
import router from '../../../router/functions/router.functions'
import routes from '../../../router/routes.dictionary'
import { isAdminLoggedIn, isAuthenticated } from '../../../shared/functions/Auth/auth.functions'
import { UserRole } from '../../functions/UserRole/userRoleManagement.types'
import { getRoleFromPath, getUserRole, setUserRole } from '../../functions/UserRole/userRoleManagment.functions'
import styles from './Header.styles'
import { HeaderProps } from './Header.types'

const pages = [
  routes.home,
  routes.shop,
  routes.send,
  routes.cart,
  routes.offers,
  routes.orders,
  routes.fulfillmentOrders,
  routes.myTrips,
  routes.requests
]

const Header: React.FC<HeaderProps> = (props) => {
  const { sticky = true } = props

  const navigate = router.navigate()
  const currentPage = router.getCurrentPage()

  const [currentUserRole, setCurrentUserRole] = React.useState<UserRole>(
    (getUserRole() as UserRole) || UserRole.traveler
  )

  useEffect(() => {
    setUserRole(currentUserRole)
  }, [currentUserRole])

  useEffect(() => {
    const roleFromStorage = getUserRole()

    if (roleFromStorage) {
      setCurrentUserRole(roleFromStorage as UserRole)

      return
    }

    // Infer role from routes dictionary if no stored role exists
    const roleFromPath = getRoleFromPath(currentPage.path)

    if (roleFromPath) {
      setCurrentUserRole(roleFromPath)
    }
  }, [currentPage.path])

  const isSticky = React.useMemo(() => {
    switch (currentPage.path) {
      case routes.cart.path:
        return false

      default:
        return sticky
    }
  }, [currentPage.path, sticky])

  const getFilteredPages = () => {
    return pages.filter((page) => {
      if (!page?.userRole) {
        return true
      }

      if ((page.isAuthenticationRequired || page.path === routes.cart.path) && !isAuthenticated()) {
        return false
      }

      // Show Shop, Home, and Send pages when user is not authenticated
      if (
        (page.path === routes.shop.path || page.path === routes.home.path || page.path === routes.send.path) &&
        !isAuthenticated()
      ) {
        return true
      }

      // Always show Offers page when user is not authenticated, regardless of current page
      if (page.path === routes.offers.path && !isAuthenticated()) {
        return true
      }

      return page?.userRole.includes(currentUserRole)
    })
  }

  const getNavigationLinks = () => {
    const filteredPages = getFilteredPages()

    return (
      <Box sx={styles.navLinks}>
        {filteredPages.map((page, index) => {
          const isActive = currentPage.path === page?.path

          return (
            <Button
              buttonType="tertiary"
              text={page?.name}
              onClick={() => navigate(page?.path)}
              isNav
              isActiveNav={isActive}
              style={styles.button}
              key={index}
            />
          )
        })}
      </Box>
    )
  }

  const logo = (centre: boolean = false) => {
    let homePath = ''

    switch (currentUserRole) {
      case UserRole.shopper:
        homePath = routes.shop.path
        break

      case UserRole.sender:
        homePath = routes.send.path
        break

      case UserRole.traveler:
        homePath = routes.home.path
        break
    }

    return (
      <Box sx={centre ? styles.logoCentre : styles.logoContainer} onClick={() => navigate(homePath)}>
        <img src={images.hourrierLogo} alt="Logo" style={styles.logo} />
      </Box>
    )
  }

  const getRightSide = () => {
    if (isAuthenticated()) {
      return (
        <Box sx={styles.rightSideArea}>
          <ResponsiveDisplayWrapper isDesktop={true}>
            <RoleSwitcher currentRole={currentUserRole} />
          </ResponsiveDisplayWrapper>

          <ProfileMenu isAuthenticated={true} />
        </Box>
      )
    } else {
      return (
        <>
          <ResponsiveDisplayWrapper isDesktop={true}>
            <AuthButtons currentPage={currentPage} />
          </ResponsiveDisplayWrapper>

          <ResponsiveDisplayWrapper isDesktop={false}>
            <ProfileMenu isAuthenticated={false} />
          </ResponsiveDisplayWrapper>
        </>
      )
    }
  }

  const isOnDashboardPage = React.useMemo(() => {
    const currentPath = currentPage.path

    return (
      currentPath === routes.adminDashboard.path ||
      currentPath === routes.logisticsPartnerDashboard.path ||
      currentPath.startsWith(`${routes.adminDashboard.path}/`) ||
      currentPath.startsWith(`${routes.logisticsPartnerDashboard.path}/`)
    )
  }, [currentPage.path])

  if (isAdminLoggedIn() || isOnDashboardPage) {
    return null
  } else {
    return (
      <AppBar
        color="transparent"
        sx={{
          ...styles.header,
          position: {
            xs: isSticky ? 'sticky' : 'static',
            md: 'static'
          }
        }}
      >
        <Box>
          <Toolbar disableGutters>
            {logo()}

            <Box sx={styles.leftSideArea}>
              <OverFlowMenu pages={getFilteredPages()} />
              {logo(true)}
            </Box>

            {getNavigationLinks()}

            {getRightSide()}
          </Toolbar>
        </Box>
      </AppBar>
    )
  }
}

export default Header
